import { faArrowLeft, faSearch } from '@fortawesome/pro-light-svg-icons';
import { Dispatch, SetStateAction } from 'react';
import { tv } from 'tailwind-variants';
import { useRouter } from 'next/navigation';
import { DataTestIdEnum } from '@medprev-web-app/datatestid';

import { useStickyHeader } from '@/app/(main)/_components/search-modal/_hooks/use-sticky-header.hook';
import { Button } from '@/components/ui/button/button.component';
import { Icon } from '@/components/ui/icon.component';
import { Input } from '@/components/ui/input/input.component';
import { useProgressiveModal } from '@/components/ui/progressive-modal/progressive-modal.hook';
import { SheetHeader } from '@/components/ui/sheet';
import {
  ProgressiveSearchModalKeys,
  SelectedProcedures,
} from '@/app/(main)/_components/search-modal/search-modal.types';
import { SelectMoreResults } from '@/app/(main)/_components/search-modal/_components/select-more-results.component';
import { useSelectMoreExams } from '@/app/(main)/_components/search-modal/_hooks/use-select-more-exams.hook';
import { Typography } from '@/components/ui/typography/typography.component';
import { InfiniteScroll } from '@/components/ui/infinite-scroll.component';
import { buildSearchResult } from '@/utils/search/search-result.util';
import { appendRecentSearches, useSearchState } from '@/store/search/search.store';
import { isVaccineSearchMode } from '@/utils/search/search-vaccine.util';
import { useLoading } from '@/hooks/use-loading.hook';
import { useSearch } from '@/app/_hooks/search/use-search.hook';
import { getLogger } from '@/logger/logger';
import { useRecentSearchesStorage } from '@/storage/recents-searches.storage';
import { OverridedUserLocation } from '@/utils/formatters/city/format-place-from-slug.const';

const selectedMoreExamsStyle = tv({
  slots: {
    seeMoreContainer: [
      'data-[show=false]:hidden data-[show=true]:opacity-1',
      'data-[show=false]:animate-fade-out-bottom-procedure data-[show=true]:animate-fade-in-bottom-procedure',
      'transition-all duration-300 rounded-b-md md:max-w-[800px] w-full',
      'flex justify-between items-center text-center py-2 pl-4 pr-2',
      'self-end',
      'bg-gray-100 border border-gray-300 rounded-xl',
    ],
  },
  variants: {
    isOnMobileDevice: {
      true: {
        seeMoreContainer: 'pt-[-50px]',
      },
    },
  },
});

export interface SelectMoreExamsProps {
  selectedProcedures: SelectedProcedures[];
  locationOverride?: OverridedUserLocation;
  onClear: () => void;
  setSelectProcedures: Dispatch<SetStateAction<SelectedProcedures[]>>;
}

export function SelectMoreExams({
  selectedProcedures,
  locationOverride,
  onClear,
  setSelectProcedures,
}: SelectMoreExamsProps) {
  const router = useRouter();
  const [, setStoredRecentSearches] = useRecentSearchesStorage();
  const { handleJump: jump } = useProgressiveModal<ProgressiveSearchModalKeys>();
  const { seeMoreContainer } = selectedMoreExamsStyle();
  const { containerRef, headerCallbackRef, isScrolled } = useStickyHeader();
  const {
    searchDisplayValue,
    handleSearchInput,
    inputRef,
    searchResults,
    emptySearchResults,
    isLoadingLaboratoryProcedures,
    isLoadingMoreLaboratoryProcedures,
    handleSelectProcedure,
    isChecked,
    handleLoadMore,
    hasSearchResults,
    hasNoMoreResults,
    showSeeSchedules,
    searchInputPlaceholder,
    addMoreExamsMessage,
    selectedExamsCount,
    searchType,
  } = useSelectMoreExams({
    selectedProcedures,
    setSelectProcedures,
  });
  const { isLoading, startLoading, stopLoading } = useLoading();
  const { handleSearchExamsUrl } = useSearch({ locationOverride });

  const isVaccine = isVaccineSearchMode(useSearchState.getState().selectedSearch);

  const handleJump = (key: ProgressiveSearchModalKeys, returning = false): void => {
    if (!jump) {
      return;
    }

    const searchResult = buildSearchResult(selectedProcedures, isVaccine);

    if (searchResult) {
      appendRecentSearches(searchResult, setStoredRecentSearches);
    }

    jump({ key, returning });

    if (returning && onClear) {
      onClear();
    }
  };

  const handleNavigateToScheduleResult = async (e: React.MouseEvent): Promise<void> => {
    e.preventDefault();
    startLoading();

    try {
      const searchResult = buildSearchResult(selectedProcedures, isVaccine);

      if (searchResult) {
        appendRecentSearches(searchResult, setStoredRecentSearches);
      }

      router.push(handleSearchExamsUrl(isVaccine, selectedProcedures));
    } catch (error) {
      const logger = getLogger('SearchModal - SelectedMoreExams');
      logger.error('Error updating latest searches', { selectedProcedures, isVaccine }, error);
    } finally {
      stopLoading();
    }
  };

  return (
    <div
      ref={headerCallbackRef}
      className="flex flex-col h-full"
    >
      <div
        className={`
          pt-5 px-6
          sticky top-0 left-0 right-0 z-20
          transition-colors duration-200
          ${isScrolled ? 'bg-gray-50 shadow-floating pb-3' : 'bg-white'}
        `}
      >
        <SheetHeader
          className="flex flex-row items-start gap-4 mb-4"
        >
          <button
            data-testid={DataTestIdEnum.BACK_BUTTON_SELECT_MORE_EXAMS}
            className="pt-5"
            onClick={() => {
              handleJump(ProgressiveSearchModalKeys.SEARCH, true);
            }}
          >
            <Icon icon={faArrowLeft} size="xl" className="text-gray-500 self-center cursor-pointer" />
          </button>
          <Input
            data-testid={DataTestIdEnum.SEARCH_INPUT_SELECT_MORE_EXAMS}
            placeholder={searchInputPlaceholder}
            startIcon={faSearch}
            startIconStyle="text-gray-500 text-xl"
            value={searchDisplayValue}
            onChange={handleSearchInput}
            ref={inputRef}
            className="w-full"
          />
        </SheetHeader>

        {!emptySearchResults && !isLoadingLaboratoryProcedures && (
          <Typography
            data-testid={DataTestIdEnum.SELECT_PROCEDURE_PARAGRAPH_SELECT_MORE_EXAMS}
            className="mt-1"
          >
            {addMoreExamsMessage}
          </Typography>
        )}

        {emptySearchResults && (
          <Typography
            data-testid={DataTestIdEnum.SEARCH_EMPTY_RESULTS_SELECT_MORE_EXAMS}
            className="mt-6 text-left"
          >
            Não encontramos resultados para
            {' '}
            <strong>{searchDisplayValue}</strong>
            . Tente outra palavra.
          </Typography>
        )}
      </div>

      {!emptySearchResults && (
        <div ref={containerRef} className="flex flex-col md:basis-[80vh] overflow-y-auto h-full">
          <SelectMoreResults
            isLoading={isLoadingLaboratoryProcedures}
            searchResults={searchResults}
            searchType={searchType}
            handleSelectProcedure={handleSelectProcedure}
            isChecked={isChecked}
          />

          {hasSearchResults && !isLoadingLaboratoryProcedures && (
            <InfiniteScroll
              hasMore={!hasNoMoreResults}
              isLoading={isLoadingMoreLaboratoryProcedures}
              onLoadMore={handleLoadMore}
            />
          )}
        </div>
      )}

      {selectedProcedures.length > 0 && !emptySearchResults
        && (
          <div className="p-3 shadow-modal">
            <div
              data-testid={DataTestIdEnum.SEE_MORE_CONTAINER_SELECT_MORE_EXAMS}
              data-show={selectedProcedures.length > 0 && showSeeSchedules}
              className={seeMoreContainer({
                isOnMobileDevice: showSeeSchedules && selectedProcedures.length > 0,
              })}
            >
              <Button
                data-testid={DataTestIdEnum.SELECTED_PROCEDURES_BUTTON_SELECT_MORE_EXAMS}
                variant="text"
                className="text-sm sm:text-base font-semibold w-fit"
                onClick={() => jump({ key: ProgressiveSearchModalKeys.SELECTED_OVERVIEW_EXAMS })}
              >
                {selectedExamsCount}
              </Button>

              <Button
                data-testid={DataTestIdEnum.SEE_SCHEDULES_BUTTON_SELECT_MORE_EXAMS}
                className="w-fit"
                isLoading={isLoading}
                onClick={handleNavigateToScheduleResult}
              >
                Ver horários
              </Button>
            </div>
          </div>
        )}
    </div>
  );
}
